import { ApolloClient, createHttpLink } from '@apollo/client/core';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloLink, Observable } from '@apollo/client';
import type { ErrorResponse } from '@apollo/client/link/error';
import { onError } from '@apollo/client/link/error';
import type { OperationDefinitionNode } from 'graphql';
import bugsnagClient, { breadcrumbLink } from '@getaccept/lib-shared/src/bugsnag';
import { AuthQuery } from '@getaccept/lib-shared/src/session/signing-site/session.queries';
import { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import { RootAction } from './store/root.types';
import store from './store/root.store';
import { sessionNamespace } from './session/store/session.constants';
import { SessionAction } from './session/store/session.types';
import { DocumentAction, documentNamespace } from './documents/store/document.constants';

const domain = process.env.VUE_APP_PUBLIC_API_BASE_URL || 'localhost:3002';

const abortController = new AbortController();
export const abortApollo = () => abortController.abort();
const httpLink = createHttpLink({
  uri: ({ operationName }) => `https://${domain}/graphql?op=${operationName}`,
  fetchOptions: {
    signal: abortController.signal,
  },
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
  if (graphQLErrors) {
    const sessionExpiredErrors: boolean = graphQLErrors.some(
      error =>
        error.extensions?.code === 'UNAUTHENTICATED' &&
        error.message !== 'You do not have access to this document'
    );

    const accessDenied: boolean = graphQLErrors.some(
      error =>
        error.extensions?.code === 'UNAUTHENTICATED' &&
        error.message === 'You do not have access to this document'
    );
    const recalledError: boolean = graphQLErrors.some(
      error => error.extensions?.code === 'RECALLED'
    );

    const unpublishedError: boolean = graphQLErrors.some(
      error => error.extensions?.code === 'UNPUBLISHED'
    );
    const signedDocumentError: boolean = graphQLErrors.some(
      error => error.message === 'Action not allowed for signed documents'
    );
    if (signedDocumentError) {
      store.dispatch(
        `${documentNamespace}/${[DocumentAction.SetDocumentStatus]}`,
        DocumentStatus.Signed
      );
    }
    if (recalledError) {
      store.dispatch(`${sessionNamespace}/${SessionAction.SetStatus}`, DocumentStatus.Recalled);
    }

    if (unpublishedError) {
      store.dispatch(`${sessionNamespace}/${SessionAction.SetStatus}`, DocumentStatus.Unpublished);
    }

    if (accessDenied) {
      store.dispatch(`${sessionNamespace}/${SessionAction.SetError}`);
    }

    const filteredErrors = graphQLErrors.filter(
      error =>
        !(error.extensions?.code === 'UNAUTHENTICATED' || error.extensions?.code === 'RECALLED')
    );

    if (sessionExpiredErrors && store.state.isPreview) {
      store.dispatch(RootAction.SetPreviewExpired, true);
    } else if (sessionExpiredErrors) {
      return new Observable(observer => {
        apolloClient
          .query({
            query: AuthQuery,
            variables: {
              documentId: store.state.documentId,
              recipientId: store.state.recipientId,
              authId: store.state.authId,
            },
          })
          .then(response => {
            store.dispatch(`${sessionNamespace}/${SessionAction.LoadTokenSuccess}`, {
              token: response.data.auth,
            });
            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            };
            forward(operation).subscribe(subscriber);
          })
          .catch(error => console.error('Token renegotation failed', error));
      });
    }
    if (bugsnagClient) {
      filteredErrors.forEach(graphQLError => {
        bugsnagClient.notify(new Error(JSON.stringify(graphQLError)), event => {
          event.groupingHash = graphQLError.message;
          event.addMetadata('GraphQL', {
            operationName: operation?.operationName,
            variables: operation?.variables,
            type: (operation?.query?.definitions[0] as OperationDefinitionNode)?.operation,
          });
        });
      });
    }
  }
  if (networkError && Object.keys(networkError).length && bugsnagClient) {
    bugsnagClient.notify(new Error(JSON.stringify(networkError)), event => {
      event.groupingHash = JSON.stringify(networkError);
      event.addMetadata('GraphQL', {
        operationName: operation?.operationName,
        variables: operation?.variables,
        type: (operation?.query?.definitions[0] as OperationDefinitionNode)?.operation,
      });
    });
  }
});

const getAuthToken = () => {
  const { jwt = '' } = store.state['session'].token;
  return `Bearer ${jwt}`;
};

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext({ headers: { authorization: getAuthToken() } });
  return forward(operation);
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, breadcrumbLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
});
